import React from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface CookiePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CookiePolicyModal: React.FC<CookiePolicyModalProps> = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div key="cookie-policy-modal-wrapper" className="fixed inset-0 z-[160] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/75"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-xl bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden text-left max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-white/[0.06] bg-[#322D29] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#F26522] flex items-center justify-center text-white shrink-0">
                  <i className="fas fa-cookie-bite text-sm"></i>
                </div>
                <div>
                  <h2 className="text-sm font-bold uppercase tracking-wider text-white font-heading">
                    Cookie Governance Policy
                  </h2>
                  <p className="text-[10px] text-white/60 block mt-0.5">
                    User Consent & Cookie Specifications
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Content Body */}
            <div className="p-6 sm:p-8 flex-1 overflow-y-auto custom-scrollbar space-y-6 text-white/70 text-xs leading-relaxed">
              <div className="bg-[#322D29] border border-white/[0.06] rounded-lg p-4">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#F26522] block mb-1 font-heading">Notice of Revision</span>
                <p className="text-[11px] text-white font-medium leading-relaxed">
                  We are currently finalizing our comprehensive Cookie Governance Ledger. eNvesta AI implements strictly non-invasive, GDPR-compliant functional local structures outlined below.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-white font-heading">1. Purpose and Scope</h3>
                  <p className="text-white/60 mt-1">
                    eNvesta AI uses lightweight cookies, sessionStorage, and localStorage coordinates strictly to preserve user identity sessions, secure cryptographic tokens, and persist dynamic localization metrics.
                  </p>
                </div>

                <div>
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-white font-heading">2. Cookie Classifications Employed</h3>
                  <div className="mt-2 space-y-2">
                    <div className="p-3 border border-white/[0.06] bg-[#322D29] rounded-lg">
                      <span className="font-bold text-white text-[11px] uppercase block font-heading">A. Strictly Necessary & Session Protection</span>
                      <p className="text-[11px] text-white/60 mt-1 leading-relaxed">
                        Manages your active Firestore auth context and enforces Cross-Site Request Forgery (CSRF) safety blocks. Cannot be disabled.
                      </p>
                    </div>
                    <div className="p-3 border border-white/[0.06] bg-[#322D29] rounded-lg">
                      <span className="font-bold text-white text-[11px] uppercase block font-heading">B. Localized State Storage (Preferences Cache)</span>
                      <p className="text-[11px] text-white/60 mt-1 leading-relaxed">
                        Retains currency options, metric unit choices, and diagnostic history filters to achieve zero-latency client state loading.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-white font-heading">3. Zero-Tracking Affirmation</h3>
                  <p className="text-white/60 mt-1">
                    No third-party advertising tracking scripts or behavioral telemetry pixels are integrated. Your intellectual venture data remains entirely insulated from public marketing channels.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="px-6 py-4 bg-[#322D29] border-t border-white/[0.06] flex justify-end gap-3 shrink-0">
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-[#F26522] hover:bg-[#F26522]/90 text-white text-[10px] font-bold uppercase tracking-widest rounded-lg transition-colors cursor-pointer font-heading"
              >
                Accept and Acknowledge
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
